// The dashboard project selection (architecture sprint-15 §9.3, A-11). A pure resolver plus a tiny external
// store: the Dashboard and GlobalShortcuts read the same selection, and the last project is remembered per
// user in localStorage['tt-selected-project:<userId>'] (every access in try/catch; other tabs follow through
// the storage event).
//
// Order: activity (an open shift, or a running/paused timer) always wins and locks the selector; else the only
// project; else the default project (never a shift project); else the remembered one; else nothing.

export const SELECTED_PROJECT_PREFIX = 'tt-selected-project:';

export type SelectionKind = 'shift' | 'standard';

export interface SelectionProject {
  id: string;
  name: string;
  color: string;
}

export interface SelectionCandidate extends SelectionProject {
  kind: SelectionKind;
}

/** What is running now: an open shift (always a project) or a timer (a project or "No project"). */
export interface SelectionActivity {
  kind: SelectionKind;
  project: SelectionProject | null;
}

export interface ResolvedSelection {
  /** null only for a timer on "No project". */
  project: SelectionProject | null;
  kind: SelectionKind;
  /** true while a timer or shift decides the selection. */
  locked: boolean;
}

export interface SelectionInputs {
  candidates: readonly SelectionCandidate[];
  activity: SelectionActivity | null;
  defaultProjectId: string | null;
  storedId: string | null;
}

function pick(c: SelectionCandidate): ResolvedSelection {
  return { project: { id: c.id, name: c.name, color: c.color }, kind: c.kind, locked: false };
}

export function resolveSelection(i: SelectionInputs): ResolvedSelection | null {
  if (i.activity) return { project: i.activity.project, kind: i.activity.kind, locked: true };
  const only = i.candidates.length === 1 ? i.candidates[0] : undefined;
  if (only) return pick(only);
  const byId = (id: string | null): SelectionCandidate | undefined => (id === null ? undefined : i.candidates.find((c) => c.id === id));
  const fallback = byId(i.defaultProjectId) ?? byId(i.storedId);
  return fallback ? pick(fallback) : null;
}

// ── storage + store ──

const memory = new Map<string, string>();
const listeners = new Set<() => void>();

function storageKey(userId: string): string {
  return `${SELECTED_PROJECT_PREFIX}${userId}`;
}

export function readStoredProject(userId: string): string | null {
  const key = storageKey(userId);
  try {
    const v = window.localStorage.getItem(key);
    if (v !== null) return v;
  } catch {
    // fall through to memory
  }
  return memory.get(key) ?? null;
}

function notify(): void {
  for (const l of listeners) l();
}

export function storeSelectedProject(userId: string, projectId: string): void {
  if (readStoredProject(userId) === projectId) return;
  const key = storageKey(userId);
  memory.set(key, projectId);
  try {
    window.localStorage.setItem(key, projectId);
  } catch {
    // Storage blocked: remembered for this page only.
  }
  notify();
}

/** useSyncExternalStore subscription: local writes and other tabs' storage events. */
export function subscribeSelection(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (e: StorageEvent): void => {
    if (e.key === null || e.key.startsWith(SELECTED_PROJECT_PREFIX)) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** Test seam. */
export function resetProjectSelectionForTests(): void {
  memory.clear();
  listeners.clear();
}
